const OAUTH_STATE_CONTEXT_PREFIX = 'qiyun-oauth-state:v1:';
const OAUTH_STATE_COMPACT_PREFIX = 'q1.';

export interface IOAuthStateContext {
  clientID: string;
  provider?: string;
  redirect?: string;
}

/** encodeOAuthState 保持旧版十六进制编码协议，供历史回调继续按 clientID 解码。 */
export function encodeOAuthState(value: string): string {
  if (!value) return value;
  return Array.from(new TextEncoder().encode(value))
    .map(byte => byte.toString(16).padStart(2, '0'))
    .join('');
}

/** decodeOAuthState 解码旧版十六进制 state，无法识别时按历史行为原样返回。 */
export function decodeOAuthState(value: string): string {
  if (!value) return value;
  if (!/^[0-9a-f]+$/i.test(value) || value.length % 2 !== 0) {
    return value;
  }
  try {
    const bytes = new Uint8Array(value.match(/.{1,2}/g)?.map(byte => Number.parseInt(byte, 16)) ?? []);
    return new TextDecoder().decode(bytes);
  } catch {
    return value;
  }
}

/** encodeBase64URL 使用浏览器通用的 URL-safe Base64，避免邀请 token 经十六进制后长度翻倍。 */
function encodeBase64URL(value: string): string {
  const binary = Array.from(new TextEncoder().encode(value), byte => String.fromCharCode(byte)).join('');
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/** decodeBase64URL 还原 OAuth state 中的 UTF-8 上下文，非法输入交由调用方拒绝。 */
function decodeBase64URL(value: string): string {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
  const binary = atob(padded);
  return new TextDecoder().decode(Uint8Array.from(binary, char => char.charCodeAt(0)));
}

/** encodeOAuthStateContext 紧凑携带 provider、clientID 与安全业务地址，并保留旧调用的编码兼容性。 */
export function encodeOAuthStateContext(clientID: string, redirect?: string, provider?: string): string {
  if (!redirect && !provider) return encodeOAuthState(clientID);
  return `${OAUTH_STATE_COMPACT_PREFIX}${encodeBase64URL(JSON.stringify({
    clientID,
    ...(provider ? { provider } : {}),
    ...(redirect ? { redirect } : {}),
  }))}`;
}

/** decodeOAuthStateContext 同时解析新版上下文和旧版纯 clientID state，损坏的新格式直接拒绝。 */
export function decodeOAuthStateContext(value: string): IOAuthStateContext {
  if (value.startsWith(OAUTH_STATE_COMPACT_PREFIX)) {
    try {
      const context = JSON.parse(decodeBase64URL(value.slice(OAUTH_STATE_COMPACT_PREFIX.length))) as Partial<IOAuthStateContext>;
      if (typeof context.clientID !== 'string' || !context.clientID) return { clientID: '' };
      return {
        clientID: context.clientID,
        ...(typeof context.provider === 'string' && context.provider ? { provider: context.provider } : {}),
        ...(typeof context.redirect === 'string' && context.redirect ? { redirect: context.redirect } : {}),
      };
    } catch {
      return { clientID: '' };
    }
  }

  const decoded = decodeOAuthState(value);
  if (!decoded.startsWith(OAUTH_STATE_CONTEXT_PREFIX)) {
    return { clientID: decoded };
  }

  try {
    const context = JSON.parse(decoded.slice(OAUTH_STATE_CONTEXT_PREFIX.length)) as Partial<IOAuthStateContext>;
    if (typeof context.clientID !== 'string' || !context.clientID) return { clientID: '' };
    return {
      clientID: context.clientID,
      ...(typeof context.provider === 'string' && context.provider ? { provider: context.provider } : {}),
      ...(typeof context.redirect === 'string' && context.redirect ? { redirect: context.redirect } : {}),
    };
  } catch {
    return { clientID: '' };
  }
}
